import { useMemo } from 'react'
import clsx from 'clsx'
import { useLingui } from '@lingui/react/macro'
import { HugeiconsIcon } from '@hugeicons/react'

import {
  ArrowLeft02Icon,
  PaintBrush04Icon,
  Layers02Icon,
  InboxIcon,
  Megaphone01Icon,
  HelpCircleIcon,
  // Settings02Icon,
  Calendar03Icon,
  Key01Icon,
  StackStarIcon,
  QrCode01Icon,
  Comment01Icon,
  BedDoubleIcon,
  UserGroup02Icon
} from '@hugeicons/core-solid-rounded'

import { Permission, useCheckPermission } from '@/features/auth'
import { Item, ItemGroup, OrganizationItem, UserItem } from './Item'
import { useIsOpen } from '../hooks/useIsOpen'
import { usePathChange } from '../hooks/usePathChange'
import { useHasUnreadFeedbacks } from '@/features/feedback'
import { useHasUnreadOrders } from '@/features/order'

const Sidebar: React.FC = function () {
  const { t, i18n } = useLingui()

  const [isOpen, toggleOpen] = useIsOpen()
  usePathChange()

  const hasUnreadFeedbacks = useHasUnreadFeedbacks()
  const hasUnreadOrders = useHasUnreadOrders()

  const canChangeBranding = useCheckPermission([Permission.BRANDING_UPDATE])
  const canSeeLayout = useCheckPermission([Permission.LAYOUT_READ])
  const canSeeOrders = useCheckPermission([Permission.ORDER_READ])
  const canSeeStays = useCheckPermission([Permission.STAY_READ])
  const canSeeFeedbacks = useCheckPermission([Permission.FEEDBACK_READ])
  const canSeeSpaces = useCheckPermission([Permission.SPACE_READ])
  const canSeeGuests = useCheckPermission([Permission.GUEST_READ])

  const sidebarClass = clsx(
    'fixed inset-0 z-sidebar md:relative',
    'flex h-full shrink grow flex-col items-start overflow-hidden rounded-tr bg-secondary-dark transition-all',
    isOpen && 'max-md:translate-x-0 md:w-[300px]',
    !isOpen && 'max-md:-translate-x-full md:w-[70px]'
  )

  const guestAppItems = useMemo(() => {
    const base = [
      {
        id: 'goodies',
        path: '/goodies',
        Icon: StackStarIcon,
        label: 'Goodies'
      },
      {
        id: 'feed',
        path: '/feed',
        Icon: Megaphone01Icon,
        label: 'Feed'
      }
    ]

    if (canSeeLayout) {
      base.push({
        id: 'layout',
        path: '/layout',
        Icon: Layers02Icon,
        label: t`Categories`
      })
    }

    if (canChangeBranding) {
      base.push({
        id: 'branding',
        path: '/branding',
        Icon: PaintBrush04Icon,
        label: 'Branding'
      })
    }

    base.push({
      id: 'qr-codes',
      path: '/qr-codes',
      Icon: QrCode01Icon,
      label: 'QR Codes'
    })

    return base
  }, [canSeeLayout, canChangeBranding, t])

  const adminItems = useMemo(() => {
    const base = []

    if (canSeeOrders) {
      base.push(
        ...[
          {
            id: 'requests',
            path: '/requests',
            Icon: InboxIcon,
            label: t`Requests`,
            markAsUnread: hasUnreadOrders
          },
          {
            id: 'events',
            path: '/events',
            Icon: Calendar03Icon,
            label: t`Events`
          }
        ]
      )

      if (canSeeStays) {
        base.push({
          id: 'stays',
          path: '/stays',
          Icon: Key01Icon,
          label: t`Stays`
        })
      }

      if (canSeeSpaces) {
        base.push({
          id: 'rooms',
          path: '/rooms',
          Icon: BedDoubleIcon,
          label: t`Rooms`
        })
      }

      if (canSeeGuests) {
        base.push({
          id: 'guests',
          path: '/guests',
          Icon: UserGroup02Icon,
          label: t`Guests`
        })
      }

      if (canSeeFeedbacks) {
        base.push({
          id: 'feedbacks',
          path: '/feedbacks',
          Icon: Comment01Icon,
          label: t`Feedbacks`,
          markAsUnread: hasUnreadFeedbacks
        })
      }
    }

    return base
  }, [
    canSeeStays,
    canSeeOrders,
    canSeeFeedbacks,
    canSeeSpaces,
    canSeeGuests,
    hasUnreadFeedbacks,
    hasUnreadOrders,
    t
  ])

  return (
    <nav className={sidebarClass}>
      <div className="flex w-full flex-row items-center justify-end gap-2.5 px-4 py-3">
        <button
          onClick={toggleOpen}
          className={clsx(
            'size-7 text-white transition-[rotate] rounded-small',
            !isOpen && 'rotate-180'
          )}
        >
          <HugeiconsIcon icon={ArrowLeft02Icon} className="size-full" />
        </button>
      </div>
      <div className="flex w-full shrink grow flex-col gap-6 overflow-y-auto overflow-x-hidden py-2.5 pl-1">
        <ItemGroup
          isSidebarOpen={isOpen}
          title={t`WebApp`}
          items={guestAppItems}
        />
        <ItemGroup
          isSidebarOpen={isOpen}
          title={t`Admin Area`}
          items={adminItems}
        />
      </div>
      <footer className="flex w-full flex-col gap-2.5 p-3 pr-0">
        <Item
          Icon={HelpCircleIcon}
          path={`/${i18n.locale}/support`}
          target="_blank"
          label={t`Support`}
          isSidebarOpen={isOpen}
          id="support"
          useInternalLink={false}
        />
        {/* For now we don't have Settings, but we will add this back at some point */}
        {/* <Item
          Icon={Settings02Icon}
          path="/settings"
          label={t`Settings`}
          isSidebarOpen={isOpen}
          id="settings"
        /> */}
        <OrganizationItem isSidebarOpen={isOpen} />
        <UserItem isSidebarOpen={isOpen} />
      </footer>
    </nav>
  )
}

export default Sidebar
